/**
 * Environment configuration with TypeScript types
 * Provides type-safe access to import.meta.env
 */

// Environment configuration object with validation
class APEnvironment {
  private static instance: APEnvironment;

  public readonly MODE: string;
  public readonly API_BASE_URL: string;
  public readonly APP_TITLE: string;
  public readonly APP_VERSION: string;
  public readonly APP_NAME: string;
  public readonly APP_URL: string;
  public readonly APP_PUBLIC_FOLDER: string;
  public readonly APP_RESOURCES_BASE_URL: string;
  public readonly LOCAL_PROXY: boolean;
  public readonly USE_USER_DETAIL_MOCK: boolean;
  public readonly IS_PRODUCTION: boolean;
  public readonly IS_DEVELOPMENT: boolean;
  public readonly IS_TEST: boolean;
  public readonly APP_ENV: string;
  public readonly IS_PR_ENV_ROUTING_AVAILABLE: boolean;

  private constructor() {
    // Validate required environment variables
    this.validateRequiredEnvVars();
    this.MODE = import.meta.env.MODE;
    // Initialize configuration
    this.API_BASE_URL = import.meta.env.AP_API_BASE_URL;
    this.APP_TITLE = import.meta.env.AP_APP_TITLE || 'Internal Panel';
    this.APP_NAME = import.meta.env.AP_APP_NAME || 'Internal Panel';
    this.APP_VERSION = import.meta.env.AP_APP_VERSION || '1.0.0';
    this.APP_URL = import.meta.env.AP_APP_URL || '';
    this.APP_PUBLIC_FOLDER = import.meta.env.AP_APP_PUBLIC_URL || '';
    this.APP_RESOURCES_BASE_URL = import.meta.env.AP_APP_RESOURCES_BASE_URL || '';

    // Proxy Configuration
    this.LOCAL_PROXY = import.meta.env.AP_APP_LOCAL_PROXY || false;

    // Feature toggles / flags
    this.USE_USER_DETAIL_MOCK = (import.meta.env.AP_USE_USER_DETAIL_MOCK ?? 'false') === 'true';

    // Environment flags
    this.IS_PRODUCTION = import.meta.env.PROD;
    this.IS_DEVELOPMENT = import.meta.env.DEV;
    this.IS_TEST = import.meta.env.MODE === 'test';

    // Which deployed environment this is, per env/.env.* — 'development' | 'test' | 'production'.
    this.APP_ENV = import.meta.env.AP_APP_ENV || '';

    // Ephemeral PR-env routing (INF-856) is a TST affordance: PR pods only exist there.
    //
    // Deliberately AP_APP_ENV and not the flags above, which are wrong for this question:
    // `npm run build:test` produces import.meta.env.PROD === true (so IS_PRODUCTION is true on the
    // TST deploy) and MODE === 'test' (so IS_TEST is also true under vitest). AP_APP_ENV is the only
    // signal specific to the TST deployment — the header component already relies on it.
    //
    // An allowlist rather than `!== 'production'`, so a build with the var missing fails closed.
    // 'development' is included because `npm run dev:mock` is the only way to drive this UI locally.
    this.IS_PR_ENV_ROUTING_AVAILABLE = this.APP_ENV === 'test' || this.APP_ENV === 'development';
  }

  public static getInstance(): APEnvironment {
    if (!APEnvironment.instance) {
      APEnvironment.instance = new APEnvironment();
    }
    return APEnvironment.instance;
  }

  private validateRequiredEnvVars(): void {
    const required = ['AP_API_BASE_URL'];
    const missing = required.filter((key) => !import.meta.env[key as keyof ImportMetaEnv]);

    if (missing.length > 0) {
      throw new Error(
        `Missing required environment variables: ${missing.join(', ')}\n` +
          'Please check your .env file or environment configuration.'
      );
    }
  }

  /**
   * Get the full API URL for a given endpoint
   */
  public getApiUrl(endpoint: string): string {
    const baseUrl = this.API_BASE_URL.replace(/\/+$/, ''); // Remove trailing slashes
    const path = endpoint.replace(/^\/+/, ''); // Remove leading slashes
    return `${baseUrl}/${path}`;
  }

  /**
   * Check if feature flag is enabled.
   * Reads AP_FEATURE_<KEY> env var — the AP_ prefix is required because Vite
   * is configured with envPrefix: ['AP_'] (see vite.config.ts), so env vars
   * not starting with `AP_` are not exposed to client code.
   */
  public isFeatureEnabled(featureKey: string): boolean {
    const envKey = `AP_FEATURE_${featureKey.toUpperCase()}` as keyof ImportMetaEnv;
    return import.meta.env[envKey] === 'true';
  }
}

// Export singleton instance
export const env = APEnvironment.getInstance();

// Validation helpers
export const validateEnvironment = (): void => {
  try {
    APEnvironment.getInstance();
    console.log('✅ Environment configuration validated successfully');
  } catch (error) {
    console.error('❌ Environment validation failed:', error);
    throw error;
  }
};

// Development helpers
if (env.IS_DEVELOPMENT) {
  console.log('🔧 Development Environment Configuration:', {
    API_BASE_URL: env.API_BASE_URL,
    APP_TITLE: env.APP_TITLE,
    APP_VERSION: env.APP_VERSION,
    APP_NAME: env.APP_NAME,
    USE_USER_DETAIL_MOCK: env.USE_USER_DETAIL_MOCK,
    IS_PRODUCTION: env.IS_PRODUCTION,
    IS_DEVELOPMENT: env.IS_DEVELOPMENT,
    IS_TEST: env.IS_TEST,
  });
}
